import { useEffect, useRef, useCallback } from 'react';
import { useLogin } from '../context/LoginContext';
import { useSettings } from '../context/SettingsContext';
import { MAX_AUTO_LOGOUT_MINUTES } from '../constants/autoLogout';
import logger from '../utils/logger';

const LAST_ACTIVITY_STORAGE_KEY = 'ti_last_activity_timestamp';
const ACTIVITY_PERSIST_INTERVAL = 60000; // Persist last activity every 60 seconds (1 minute)

// A throttle function to limit how often a function can be called.
const throttle = (func, limit) => {
  let inThrottle = false;
  let trailingCall = false;
  let lastArgs = null;
  let lastContext = null;

  return function() {
    lastContext = this;
    lastArgs = arguments;

    if (!inThrottle) {
      inThrottle = true;
      func.apply(lastContext, lastArgs);

      setTimeout(() => {
        inThrottle = false;
        if (trailingCall) {
          func.apply(lastContext, lastArgs);
          trailingCall = false;
        }
      }, limit);
    } else {
      // A call came in during the throttle period.
      // Mark that we should make one more call after the period ends.
      trailingCall = true;
    }
  };
};

const MAX_TIMEOUT_MS = MAX_AUTO_LOGOUT_MINUTES * 60 * 1000;

/**
 * Get the auto-logout timeout in milliseconds from settings
 * @param {Object} settings - Settings object from SettingsContext
 * @returns {number} Timeout in ms, or 0 if disabled
 */
const getAutoLogoutTimeoutMs = (settings) => {
  try {
    const autoLogoutMinutes = parseInt(settings?.autoLogoutTimer || '0', 10);
    if (autoLogoutMinutes <= 0) {
      return 0;
    }
    
    // Cap at maximum to prevent setTimeout overflow
    const cappedMinutes = Math.min(autoLogoutMinutes, MAX_AUTO_LOGOUT_MINUTES);
    const timeoutMs = cappedMinutes * 60 * 1000;
    
    // Additional safety check for setTimeout overflow
    if (timeoutMs > MAX_TIMEOUT_MS) {
      logger.warn(`Auto-logout timeout ${autoLogoutMinutes} minutes exceeds maximum, capping to ${MAX_AUTO_LOGOUT_MINUTES} minutes`);
      return MAX_TIMEOUT_MS;
    }
    
    return timeoutMs;
  } catch (e) {
    logger.error('Could not parse auto-logout timer from settings', e);
    return 0;
  }
};

/**
 * Update the last activity timestamp in localStorage
 */
const updateLastActivityTimestamp = () => {
  try {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString());
  } catch (e) {
    logger.error('Could not update last activity timestamp', e);
  }
};

/**
 * Check if auto-logout should occur based on stored last activity timestamp.
 * This is called on app load to handle the case where the browser was closed
 * but the inactivity period has elapsed.
 * @param {Object} settings - Settings object from SettingsContext
 * @returns {boolean} True if should logout due to inactivity
 */
const shouldLogoutOnLoad = (settings) => {
  const timeoutMs = getAutoLogoutTimeoutMs(settings);
  
  // If auto-logout is disabled, no need to check
  if (timeoutMs <= 0) {
    return false;
  }

  try {
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY);
    if (!lastActivityStr) {
      // No stored timestamp - this could be first use or cleared storage
      // Don't logout, but set the timestamp now
      updateLastActivityTimestamp();
      return false;
    }

    const lastActivity = parseInt(lastActivityStr, 10);
    
    // Handle corrupted timestamp data
    if (isNaN(lastActivity)) {
      logger.warn('Corrupted activity timestamp in localStorage, resetting');
      updateLastActivityTimestamp();
      return false;
    }

    const elapsed = Date.now() - lastActivity;

    if (elapsed >= timeoutMs) {
      logger.info(`Auto-logout on load: ${Math.round(elapsed / 60000)} minutes of inactivity (timeout: ${Math.round(timeoutMs / 60000)} minutes)`);
      return true;
    }
  } catch (e) {
    logger.error('Error checking last activity timestamp', e);
  }

  return false;
};

/**
 * Clear the last activity timestamp (called on logout)
 */
const clearLastActivityTimestamp = () => {
  try {
    localStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY);
  } catch (e) {
    logger.error('Could not clear last activity timestamp', e);
  }
};

const useAutoLogout = () => {
  const { logout, isAuthenticated } = useLogin();
  const { settings } = useSettings();
  const timerId = useRef(null);
  const persistIntervalId = useRef(null);
  const hasCheckedOnLoad = useRef(false);
  const logoutTriggered = useRef(false);

  const handleLogout = useCallback(async () => {
    logger.info('Auto-logout due to inactivity.');
    logoutTriggered.current = true;
    // Clear the persist interval immediately to stop timestamp updates
    if (persistIntervalId.current) {
      clearInterval(persistIntervalId.current);
      persistIntervalId.current = null;
    }
    clearLastActivityTimestamp();
    try {
      await logout('inactivity');
    } catch (err) {
      logger.error('Auto-logout failed:', err);
    }
  }, [logout]);

  const startTimers = useCallback(() => {
    const timeoutMs = getAutoLogoutTimeoutMs(settings);

    // If timer is disabled (set to 0), do nothing.
    if (timeoutMs <= 0) {
      return;
    }

    // Update last activity timestamp immediately when timers start/reset
    updateLastActivityTimestamp();

    // Set the main logout timer
    timerId.current = setTimeout(handleLogout, timeoutMs);
  }, [handleLogout, settings]);

  const resetTimers = useCallback(() => {
    if (timerId.current) clearTimeout(timerId.current);
    startTimers();
  }, [startTimers]);

  // Check on initial load if we should logout due to elapsed inactivity
  useEffect(() => {
    if (!isAuthenticated || hasCheckedOnLoad.current) {
      return;
    }

    hasCheckedOnLoad.current = true;

    if (shouldLogoutOnLoad(settings)) {
      logoutTriggered.current = true;
      handleLogout();
    }
  }, [isAuthenticated, handleLogout, settings]);

  // Effect to set up and clean up event listeners
  useEffect(() => {
    // Don't set up timers if not authenticated or if logout was already triggered
    if (!isAuthenticated || logoutTriggered.current) {
      return;
    }

    // Throttle the reset function to avoid performance issues with frequent events like 'mousemove'.
    // We'll reset the timer at most once every 5 seconds.
    const throttledReset = throttle(resetTimers, 5000);

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];

    // Listen for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, throttledReset);
    });

    // Listen for settings changes (e.g., user changes auto-logout timer)
    const handleSettingsChange = () => {
      resetTimers();
    };
    window.addEventListener('settings-changed', handleSettingsChange);

    // Start timers on initial load
    startTimers();

    // Periodically persist last activity timestamp to localStorage
    // This ensures the timestamp is saved even if the browser is force-closed
    persistIntervalId.current = setInterval(() => {
      const timeoutMs = getAutoLogoutTimeoutMs(settings);
      if (timeoutMs > 0) {
        updateLastActivityTimestamp();
      }
    }, ACTIVITY_PERSIST_INTERVAL);

    // Cleanup function
    return () => {
      activityEvents.forEach(event => window.removeEventListener(event, throttledReset));
      window.removeEventListener('settings-changed', handleSettingsChange);
      if (timerId.current) clearTimeout(timerId.current);
      if (persistIntervalId.current) clearInterval(persistIntervalId.current);
    };
  }, [isAuthenticated, resetTimers, startTimers, settings]);
};

export default useAutoLogout;